/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import BASEUtils from '../../../../../@BASE/utils/BASEUtils';

// Global Data Components
// =========================================================

export const ReviewData = {
  development: {
    status: 'development',
    approved: false,
    approval_date: ''
  },
  production: {
    status: 'not_started',
    approved: false,
    approval_date: ''
  }
};

export const eMentorObject = {
  id: BASEUtils.generateGUID(),
  review: ReviewData,
  title: '',
  content: '',
  source: ''
};

export const eMentorArray = [eMentorObject];

export const MediaObject = {
  id: BASEUtils.generateGUID(),
  review: ReviewData,
  type: 'image', // overwritten by certain templates to be video, audio or pdf
  source_url: '',
  media_url: '',
  title_tl: '',
  title_en: '',
  description: '', // optional, used in images
  transcript: '', // optional, used in videos
  display_content: '', // optional, used in pdf
  author: '',
  author_desc_tl: '', // optional, used in videos/pdf
  author_desc_en: '', // optional, used in videos/pdf
  published_date: '',
  downloaded_date: '',
  cc_license: ''
};

export const MaterialsObject = [
  {
    material_title_en: '',
    material_title_tl: '',
    source_url: '',
    media_url: '',
    media_material: false,
    media_page_id: ''
  }
];

export const DevelopedVideoSegments = [
  {
    id: BASEUtils.generateGUID(),
    review: ReviewData,
    description: '',
    text_tl: '',
    // text_en: "",
    start_minute: 0,
    start_second: 0,
    end_minute: 0,
    end_second: 0
  }
];

export const IntroVideoSegments = [
  {
    id: BASEUtils.generateGUID(),
    review: ReviewData,
    text_en: '',
    start_minute: 0,
    start_second: 0,
    end_minute: 0,
    end_second: 0
  }
];

const DefaultData = {
  // Lesson Template Data
  // =========================================================

  // Introduction Video
  TMPL001: {
    core: DevelopedVideoSegments,
    description: '',
    transcript: '',
    eMentor: []
  },

  // Objectives
  TMPL002: {
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        tag: '',
        media: MediaObject,
        title_tl: '',
        desc_tl: '',
        title_en: '',
        desc_en: ''
      }
    ],
    eMentor: []
  },

  // Scenario Setter
  TMPL003: {
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        media: MediaObject,
        text_tl: '',
        text_en: ''
      }
    ],
    eMentor: []
  },

  // Terminology Review
  TMPL004: {
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        term_tl: '',
        term_en: '',
        definition: '',
        synonym: '',
        antonym: '',
        sentence: '',
        pronunciation: ''
      }
    ],
    eMentor: []
  },

  // Reading
  TMPL005: {
    media: MediaObject,
    core: {
      ...MediaObject,
      type: 'pdf'
    },
    focus_points: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        linked_item: '',
        focus_header: '',
        focus_content: ''
      }
    ],
    eMentor: []
  },

  // Listening
  TMPL006: {
    media: {
      ...MediaObject,
      type: 'video'
    },
    core: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      start_minute: 0,
      start_second: 0,
      end_minute: 0,
      end_second: 0
    },
    cc: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        start_minute: 0,
        start_second: 0,
        end_minute: 0,
        end_second: 0,
        tl_text: ''
      }
    ],
    focus_points: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        start_minute: 0,
        start_second: 0,
        end_minute: 0,
        end_second: 0,
        title: '',
        content: ''
      }
    ],
    questions: [
      {
        start_minute: 0,
        start_second: 0,
        end_minute: 0,
        end_second: 0,
        title: '',
        template_id: '',
        content: {
          prompt: '',
          prompt_audio: false,
          lures: [],
          correct_feedback: '',
          incorrect_feedback: '',
          suggested_feedback: '',
          focus_point: ''
        }
      }
    ],
    eMentor: []
  },

  // Multiple Choice
  TMPL007: {
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        materials: MaterialsObject,
        type_id: '',
        prompt: '',
        start_minute: 0,
        start_second: 0,
        end_minute: 0,
        end_second: 0,
        lures: [],
        correct_feedback: '',
        incorrect_feedback_1: '',
        incorrect_feedback_2: '',
        incorrect_feedback_3: '',
        max_number_of_tries: 3,
        multi_select: false
      }
    ],
    eMentor: []
  },

  // Concept Categorizing
  TMPL008: {
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        materials: MaterialsObject,
        type_id: 0,
        prompt: '',
        lures: [
          {
            lure_text: '',
            lure_feedback: '',
            is_correct: false
          },
          {
            lure_text: '',
            lure_feedback: '',
            is_correct: false
          }
        ]
      }
    ],
    eMentor: []
  },

  // Error Identification
  TMPL009: {
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        materials: MaterialsObject,
        start_minute: 0,
        start_second: 0,
        end_minute: 0,
        end_second: 0,
        authentic_transcript_correct: '',
        authentic_transcript_inaccurate: '',
        lures: [
          {
            error: '',
            feedback: ''
          }
        ],
        correct_feedback: '',
        incorrect_feedback_1: '',
        incorrect_feedback_2: '',
        incorrect_feedback_3: '',
        max_number_of_tries: 3
      }
    ],
    eMentor: eMentorArray
  },

  // Summary Evaluation
  TMPL010: {
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        materials: MaterialsObject,
        prompt: '',
        option_1: '',
        option_2: '',
        lures: [
          {
            lure_text: '',
            correct_option: 1
          }
        ],
        correct_feedback: '',
        incorrect_feedback_1: '',
        incorrect_feedback_2: '',
        incorrect_feedback_3: ''
      }
    ],
    eMentor: eMentorArray
  },

  // Linguistic Evaluation
  TMPL011: {
    statements: [
      {
        materials: MaterialsObject,
        prompt: '',
        option_1: '',
        option_2: '',
        option_3: '',
        lures: [
          {
            lure_text: '',
            correct_option: 1,
            lure_correct_feedback: '',
            lure_incorrect_feedback: ''
          }
        ],
        incorrect_feedback_1: '',
        incorrect_feedback_2: ''
      }
    ],
    eMentor: eMentorArray
  },

  // Register Analysis
  TMPL012: {
    materials: MaterialsObject,
    summary: '',
    is_informal: false,
    core: [
      {
        original: '',
        statements: [
          {
            statement_text: '',
            is_correct: false,
            type: ''
          }
        ],
        correct_feedback: '',
        incorrect_feedback_1: '',
        incorrect_feedback_2: '',
        incorrect_feedback_3: '',
        max_number_of_tries: 3
      }
    ],
    eMentor: eMentorArray
  },

  // Lesson Summary
  TMPL013: {
    core: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      media: MediaObject,
      label: '',
      text_en: '',
      text_tl: '',
      no_jobaid_en: '',
      yes_jobaid_en: '',
      no_jobaid_tl: '',
      yes_jobaid_tl: '',
      pdf_text: '',
      jobaid_pdf_lead_tl: '',
      jobaid_pdf_lead_en: ''
    },
    item_feedback: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        title_en: '',
        title_tl: '',
        interaction_type: '',
        interaction_id: ''
      }
    ],
    jobaid_pdf: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        title: '',
        content_tl: '',
        content_en: ''
      }
    ],
    eMentor: []
  },

  // Module Summary
  TMPL014: {
    core: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      media: MediaObject,
      label: 'TLO',
      text_en: '',
      text_tl: ''
    },
    item_feedback: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        title_en: '',
        title_tl: '',
        interaction_type: '',
        interaction_id: ''
      }
    ]
  },

  // Enrichment
  TMPL015: {
    core: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      media: MediaObject,
      text: '',
      link: '',
      conclusion: ''
    }
  },

  // Practical Exercise Template Data
  // =========================================================

  // PE Transition
  TMPPE001: {},

  // Mission Selection
  TMPPE002: {
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        difficulty: '',
        media: MediaObject,
        description: '',
      }
    ]
  },

  // PE Brief
  TMPPE003: {
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        difficulty: '',
        description: '',
        transcript: '',
        segments: DevelopedVideoSegments
      }
    ]
  },

  // LPE Environment
  TMPPE004: {
    environment: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      character: '',
      location: '',
      description: ''
    },
    scenario: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      text_tl: '',
      text_en: ''
    },
    povs: [],
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        object: {
          id: '',
          label: '',
          device: ''
        },
        task: {
          title_tl: '',
          title_en: '',
          text_tl: '',
          text_en: '',
          type: '',
          difficulty: ''
        },
        content: [
          {
            id: '',
            conditional: false,
            conditional_id: '',
            return: false
          }
        ]
      }
    ]
  },

  // FPE Environment Home
  TMPPE005: {
    paths: [
      {
        id: ''
      }
    ]
  },

  // FPE Environment Path
  TMPPE006: {
    environment: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      character: '',
      location: '',
      description: ''
    },
    scenario: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      text_tl: '',
      text_en: ''
    },
    immediate: [], // objects will look just like the core content but should not be defaulted to anything
    povs: [],
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        object: {
          id: '',
          label: '',
          device: ''
        },
        task: {
          title_tl: '',
          title_en: '',
          text_tl: '',
          text_en: '',
          type: ''
        },
        content: [
          {
            id: '', // this is the id of the page it links to
            conditional: false, // this is a boolean to decide if the task requires something else done first
            conditional_id: '', // this is the id of the page that would need to be done first
            return: false, // this lets the imi know that after completing this page to exit the chain
            next_id: '' // This informs the imi what content id it's supposed to travel to next in order to handle branching. If it's empty it assumes next in array automatically
          }
        ]
      }
    ]
  },

  // PE Reading Authentic Material
  TMPPE007: {
    core: {
      ...MediaObject,
      type: 'pdf'
    },
    focus_points: [],
    eMentor: []
  },

  // PE Listening Authentic Material
  TMPPE008: {
    media: {
      ...MediaObject,
      type: 'video'
    },
    core: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      start_minute: 0,
      start_second: 0,
      end_minute: 0,
      end_second: 0
    },
    cc: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        start_minute: 0,
        start_second: 0,
        end_minute: 0,
        end_second: 0,
        tl_text: ''
      }
    ],
    focus_points: [],
    questions: [],
    eMentor: []
  },

  // PE Reference Reading Authentic Material
  TMPPE009: {
    material: '',
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        prompt: '',
        start_paragraph: 1,
        start_sentence: 1,
        end_paragraph: 1,
        end_sentence: 1,
        feedback: ''
      }
    ]
  },

  // PE Reference Listening Authentic Material
  TMPPE010: {
    material: '',
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        prompt: '',
        start_minute: 0,
        start_second: 0,
        end_minute: 0,
        end_second: 0,
        feedback: ''
      }
    ]
  },

  // PE Multiple Choice
  TMPPE011: {
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        materials: MaterialsObject,
        type_id: '',
        prompt: '',
        start_minute: 0,
        start_second: 0,
        end_minute: 0,
        end_second: 0,
        lures: [],
        correct_feedback: '',
        incorrect_feedback_1: '',
        multi_select: false
      }
    ],
    eMentor: []
  },

  // PE Concept Comprehension
  TMPPE012: {
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        materials: MaterialsObject,
        type_id: 0,
        prompt: '',
        lures: [
          {
            lure_text: '',
            lure_feedback: '',
            is_correct: false
          },
          {
            lure_text: '',
            lure_feedback: '',
            is_correct: false
          }
        ]
      }
    ],
    eMentor: []
  },

  // PE Supplemental Content
  TMPPE013: {
    core: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      type_id: '',
      media: MediaObject,
      start_minute: 0,
      start_second: 0,
      end_minute: 0,
      end_second: 0,
      cc: [],
      context: '',
      prompt: '',
      lures: [],
      correct_feedback: '',
      incorrect_feedback_1: '',
      incorrect_feedback_2: '',
      incorrect_feedback_3: '',
      eMentor: []
    }
  },

  // FPE Informative Event
  TMPPE014: {
    core: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      type_id: '',
      instructions: '',
      sender: '',
      tl: false,
      subject: '',
      body: '',
      transcript: '',
      sender_desc: '',
      statement: '',
      task_title: ''
    }
  },

  // FPE Decision Event
  TMPPE015: {
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        media: MediaObject,
        title_tl: '',
        text_tl: '',
        title_en: '',
        text_en: '',
        progression_id: ''
      },
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        media: MediaObject,
        title_tl: '',
        text_tl: '',
        title_en: '',
        text_en: '',
        progression_id: ''
      }
    ]
  },

  // FPE Observation Event
  TMPPE016: {
    core: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        media: MediaObject,
        text_tl: '',
        text_en: ''
      }
    ]
  },

  // PE Dialog Text
  TMPPE017: {
    dialog_segments: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        initial_prompts: [
          {
            user: '',
            use_audio: false,
            text: ''
          }
        ],
        initial_responses: [
          {
            use_audio: false,
            lure_text: '',
            is_correct: false,
            feedbacks: [
              {
                user: '',
                use_audio: false,
                text: ''
              }
            ]
          }
        ],
        self_correction_prompts: [
          {
            user: '',
            use_audio: false,
            text: ''
          }
        ],
        self_correction_responses: [
          {
            use_audio: false,
            lure_text: '',
            is_correct: false,
            feedbacks: [
              {
                user: '',
                use_audio: false,
                text: ''
              }
            ]
          }
        ]
      }
    ]
  },

  // PE Dialog Phone Call
  TMPPE018: {
    character_name: '',
    dialog_segments: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        initial_prompts: [
          {
            user: '',
            use_audio: false,
            text: ''
          }
        ],
        initial_responses: [
          {
            use_audio: false,
            lure_text: '',
            is_correct: false,
            feedbacks: [
              {
                user: '',
                use_audio: false,
                text: ''
              }
            ]
          }
        ],
        self_correction_prompts: [
          {
            user: '',
            use_audio: false,
            text: ''
          }
        ],
        self_correction_responses: [
          {
            use_audio: false,
            lure_text: '',
            is_correct: false,
            feedbacks: [
              {
                user: '',
                use_audio: false,
                text: ''
              }
            ]
          }
        ]
      }
    ]
  },

  // PE Dialog Face to Face
  TMPPE019: {
    env_desc: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      character_name: '',
      character_desc: '',
      character_img_url: '',
      scene_desc: ''
    },
    dialog_segments: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        initial_prompts: [
          {
            user: '',
            use_audio: true,
            text: '',
            cc: []
          }
        ],
        initial_responses: [
          {
            use_audio: false,
            lure_text: '',
            is_correct: false,
            feedbacks: [
              {
                user: '',
                use_audio: true,
                text: '',
                cc: []
              }
            ]
          }
        ],
        self_correction_prompts: [
          {
            user: '',
            use_audio: true,
            text: '',
            cc: []
          }
        ],
        self_correction_responses: [
          {
            use_audio: false,
            lure_text: '',
            is_correct: false,
            feedbacks: [
              {
                user: '',
                use_audio: true,
                text: '',
                cc: []
              }
            ]
          }
        ]
      }
    ]
  },

  // FPE Feedback Event
  TMPPE020: {
    core: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      text_tl: '',
      text_en: '',
      unlock_id: 0
    }
  },

  // Constructed Response - Default
  TMPPE021: {
    core: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      prompt: '',
      response: ''
    },
    eMentor: []
  },

  // Constructed Response - Draft Report
  TMPPE022: {
    prompt_instructions_tl: '',
    prompt_instructions_en: '',
    prompt: '',
    summary_prompt_tl: '',
    summary_prompt_en: '',
    response: ''
  },

  // Constructed Response - Hypothesis Response
  TMPPE023: {
    prompt: '',
    response: ''
  },

  // Constructed Response - Presentation Brief
  TMPPE024: {
    ppt_title: '',
    media: MediaObject,
    prompt_tl: '',
    prompt_en: '',
    key_points_tl: '',
    key_points_en: '',
    sa_ins_tl: '',
    sa_ins_en: '',
    response: ''
  },

  // Constructed Response - Spoken Response
  TMPPE025: {
    prompt: '',
    npc: {
      name: '',
      title: '',
    },
    core: DevelopedVideoSegments,
    response: ''
  },

  // Constructed Response - White Paper
  TMPPE026: {
    prompt_instructions_tl: '',
    prompt_instructions_en: '',
    prompt: '',
    summary_prompt_en: '',
    response: ''
  },

  // Constructed Response - Note Cards
  TMPPE027: {
    context_tl: '',
    context_en: '',
    prompt: '',
    cards: [
      {
        title: '',
        response: ''
      }
    ],
    eMentor: eMentorArray
  },

  // Constructed Response - Op-Ed Paper
  TMPPE028: {
    prompt_instructions_tl: '',
    prompt_instructions_en: '',
    prompt: '',
    summary_prompt_en: '',
    response: '',
    title: '',
    subtitle1: '',
    subtitle2: '',
    send_email: false,
    email_destination: '',
    email_subject: '',
    materials: [
      {
        id: ''
      }
    ]
  },

  // FPE Capstone Report
  TMPPE029: {
    core: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      email_destination: '',
      prompt_tl: '',
      prompt_en: '',
      response: ''
    },
    section: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      prompt: '',
      lures: [
        {
          lure_text: '',
          is_correct: true
        },
        {
          lure_text: '',
          is_correct: true
        },
        {
          lure_text: '',
          is_correct: true
        },
        {
          lure_text: '',
          is_correct: true
        },
        {
          lure_text: '',
          is_correct: false
        },
        {
          lure_text: '',
          is_correct: false
        }
      ]
    },
    notebook: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        interaction_id: ''
      }
    ]
  },

  // PE Debrief
  TMPPE030: {
    core: {
      id: BASEUtils.generateGUID(),
      review: ReviewData,
      media: MediaObject,
      label: '',
      text_tl: '',
      text_en: '',
      environment: '',
      fpe: false,
      env_path_1: '',
      env_path_2: '',
      env_path_3: '',
      no_jobaid_en: '',
      yes_jobaid_en: '',
      no_jobaid_tl: '',
      yes_jobaid_tl: '',
      pdf_text: '',
      jobaid_pdf_lead_tl: '',
      jobaid_pdf_lead_en: ''
    },
    item_feedback: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        title_en: '',
        title_tl: '',
        interaction_type: '',
        interaction_id: ''
      }
    ],
    jobaid_pdf: []
  },

  // Assessment Template Data
  // =========================================================

  // Assessment Introduction
  TMPA001: {
    media: MediaObject,
    core: {
      id: BASEUtils.generateGUID(),
      review: ReviewData
    },
    en_introduction: '',
    tl_introduction: ''
  },

  // Assessment Reading Multiple Choice
  TMPA002: {
    reading_title_en: '',
    reading_title_tl: '',
    link: '',
    material_location: 'none',
    sp_url: '',
    media_page_id: '',
    question: {
      prompt: '',
      para_num: '',
      pdf_page: 0,
      lures: [
        {
          lure_text: '',
          is_correct: false
        }
      ],
      multi_select: false
    }
  },

  // Assessment Listening Multiple Choice
  TMPA003: {
    media_title_en: '',
    media_title_tl: '',
    source_url: '',
    media_url: '',
    material_location: 'none',
    media_page_id: '',
    question: {
      prompt: '',
      start_minute: 0,
      start_second: 0,
      end_minute: 0,
      end_second: 0,
      lures: [
        {
          lure_text: '',
          is_correct: false
        }
      ],
      multi_select: false
    }
  },

  // Assessment Summary
  TMPA004: {
    media: MediaObject,
    core: {
      id: BASEUtils.generateGUID(),
      review: ReviewData
    },
    min_score: 70,
    tl_prefix: '',
    tl_passed: '',
    tl_failed: '',
    en_prefix: '',
    en_passed: '',
    en_failed: ''
  },

  // Introduction Template Data
  // =========================================================

  // Introduction Carousel
  TMPI001: {
    core: [
      {
        media: MediaObject,
        title: '',
        description: ''
      }
    ],
  },

  // Tutorial Video
  TMPI002: {
    core: IntroVideoSegments,
    transcript: '',
  },

  // Introduction Summary
  TMPI003: {
    media: MediaObject,
    text: ''
  },

  // Legacty Template Data to be removed
  // =========================================================

  QuestionSelection: {
    questions: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        materials: MaterialsObject,
        prompt: '',
        lures: [
          {
            lure_text: '',
            is_correct: false
          }
        ],
        correct_feedback: '',
        incorrect_feedback_1: '',
        incorrect_feedback_2: '',
        incorrect_feedback_3: '',
        max_number_of_tries: 3,
        multi_select: false
      }
    ],
    eMentor: eMentorArray
  },
  StatementCompletion: {
    questions: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        materials: MaterialsObject,
        prompt: '',
        lures: [
          {
            lure_text: '',
            is_correct: false,
            lure_feedback: ''
          }
        ],
        correct_feedback: '',
        incorrect_feedback_1: '',
        incorrect_feedback_2: '',
        incorrect_feedback_3: '',
        max_number_of_tries: 3,
        multi_select: false
      }
    ],
    eMentor: eMentorArray
  },
  SkillIntegrationMultipleChoice: {
    questions: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        materials: MaterialsObject,
        question_text: '',
        lures: [
          {
            lure_text: '',
            start_minute: 0,
            start_second: 0,
            end_minute: 0,
            end_second: 0,
            is_correct: false,
            lure_feedback: ''
          }
        ],
        correct_feedback: '',
        incorrect_feedback_1: '',
        incorrect_feedback_2: '',
        incorrect_feedback_3: '',
        max_number_of_tries: 3,
        multi_select: false
      }
    ],
    eMentor: eMentorArray
  },
  ListeningComprehension: {
    questions: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        materials: MaterialsObject,
        question_text: '',
        lures: [
          {
            lure_text: '',
            is_correct: false
          }
        ],
        correct_feedback: '',
        incorrect_feedback_1: '',
        incorrect_feedback_2: '',
        incorrect_feedback_3: '',
        max_number_of_tries: 3,
        multi_select: false
      }
    ],
    eMentor: eMentorArray
  },
  TranslationAnalysis: {
    questions: [
      {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        materials: MaterialsObject,
        prompt: '',
        start_minute: 0,
        start_second: 0,
        end_minute: 0,
        end_second: 0,
        lures: [
          {
            lure_text: '',
            is_correct: false
          }
        ],
        correct_feedback: '',
        incorrect_feedback_1: '',
        incorrect_feedback_2: '',
        incorrect_feedback_3: '',
        max_number_of_tries: 3,
        multi_select: false
      }
    ],
    eMentor: eMentorArray
  },
  SingleReadingComprehension: {
    prompt: '',
    lures: [
      {
        lure_text: '',
        is_correct: false
      }
    ],
    correct_feedback: '',
    incorrect_feedback_1: '',
    incorrect_feedback_2: '',
    incorrect_feedback_3: '',
    max_number_of_tries: 3,
    multi_select: false
  },
  SingleStatementCompletion: {
    prompt: '',
    lures: [
      {
        lure_text: '',
        is_correct: false,
        lure_feedback: ''
      }
    ],
    correct_feedback: '',
    incorrect_feedback_1: '',
    incorrect_feedback_2: '',
    incorrect_feedback_3: '',
    max_number_of_tries: 3,
    multi_select: false
  },
  SingleLinguisticEvaluation: {
    prompt: '',
    option_1: '',
    option_2: '',
    option_3: '',
    lures: [
      {
        lure_text: '',
        correct_option: 1,
        lure_correct_feedback: '',
        lure_incorrect_feedback: ''
      }
    ],
    incorrect_feedback_1: '',
    incorrect_feedback_2: ''
  },
  SingleTranslationAnalysis: {
    prompt: '',
    start_minute: 0,
    start_second: 0,
    end_minute: 0,
    end_second: 0,
    lures: [
      {
        lure_text: '',
        is_correct: false
      }
    ],
    correct_feedback: '',
    incorrect_feedback_1: '',
    incorrect_feedback_2: '',
    incorrect_feedback_3: '',
    max_number_of_tries: 3,
    multi_select: false
  },
  SingleTranslationAnalysisReading: {
    prompt: '',
    lures: [
      {
        lure_text: '',
        is_correct: false
      }
    ],
    correct_feedback: '',
    incorrect_feedback_1: '',
    incorrect_feedback_2: '',
    incorrect_feedback_3: '',
    max_number_of_tries: 3,
    multi_select: false
  },
  Joyride: {
    cards: [
      {
        img: {
          media_url: '',
          title: '',
          description: '',
          link: '',
          published_date: '',
          downloaded_date: '',
          author: '',
          cc_license: ''
        },
        title: '',
        description: '',
        tour: '',
        steps_img: '',
        steps: [
          {
            title: '',
            content: '',
            target: '',
            placement: ''
          }
        ]
      }
    ]
  },
};

export default DefaultData;
